<template>
  <section class="container pb-8">
    <!-- 試卷列表 -->
    <div class="row">
      <div class="col-12 col-md-6 col-lg-4 mb-3 ml-auto">
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            v-model="args.keyWord"
            placeholder="試卷名稱查詢"
            aria-label="試卷名稱查詢"
            @keydown.enter="search(0)"
          />
          <div class="input-group-prepend">
            <button class="btn btn-outline-secondary" type="button" @click="search(0)">
              <i class="fa fa-search text-muted" aria-hidden="true"></i>
            </button>
            <button class="btn btn-outline-secondary" type="button" @click="search(1)">
              清除
            </button>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="error-bg mb-3" v-if="groupedPapers.length === 0 && !isLoading">
          <div>查無試卷資料</div>
        </div>
        <div v-else>
          <div
            v-for="(group, groupIndex) in groupedPapers"
            :key="group.subjectName"
            class="subject-group mb-4"
          >
            <!-- 科目群組標題 -->
            <div
              class="subject-group__header"
              @click="toggleGroup(groupIndex)"
            >
              <h5 class="subject-group__title mb-0">
                {{ group.subjectName }}
                <span class="subject-group__count">（{{ group.papers.length }} 份）</span>
              </h5>
              <i
                class="fa"
                :class="collapsedGroups[groupIndex] ? 'fa-chevron-down' : 'fa-chevron-up'"
              ></i>
            </div>

            <!-- 科目群組內容 -->
            <ul class="paper-list" v-show="!collapsedGroups[groupIndex]">
              <li
                v-for="(paper, pIndex) in group.papers"
                :key="pIndex"
                class="paper-item"
              >
                <div class="paper-item__header">
                  <h5 class="paper-item__title">{{ paper.essayPapersName }}</h5>
                </div>
                <div class="paper-item__content">
                  <div class="paper-item__info">
                    <div class="info-item">
                      <i class="fa fa-calendar mr-2"></i>
                      <span>
                        作答期限：
                        {{ paper.startDate | toTaipei | moment('YYYY-MM-DD') }} -
                        {{ paper.endDate | toTaipei | moment('YYYY-MM-DD') }}
                      </span>
                    </div>
                    <div v-if="paper.status === 2" class="info-item">
                      <i class="fa fa-trophy mr-2"></i>
                      <span>分數：{{ paper.totalScore || '--' }}</span>
                    </div>
                    <div class="info-item text-warning font-weight-bold">
                      <i class="fa fa-clock-o mr-1"></i>
                      <span>作答時間：{{ durationSecond(paper.durationSecond) || '--' }}分鐘</span>
                    </div>
                  </div>
                  <div class="paper-item__actions">
                    <button
                      class="btn btn-primary"
                      @click="handlePaperClick(paper)"
                    >
                      {{ getButtonText(paper) }}
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { GetStudentEssayPapersList } from '@/api/EssayPapersApi';

export default {
  data() {
    return {
      args: { keyWord: '' },
      papersList: [],
      courseId: null,
      courseName: '',
      isLoading: false,
      collapsedGroups: [],
    };
  },
  computed: {
    groupedPapers() {
      const keyword = this.args.keyWord ? this.args.keyWord.trim().toLowerCase() : '';
      const filtered = keyword
        ? this.papersList.filter((p) => (p.essayPapersName || '').toLowerCase().includes(keyword))
        : this.papersList;

      const map = {};
      filtered.forEach((paper) => {
        const key = paper.subjectName || '未分類';
        if (!map[key]) map[key] = [];
        map[key].push(paper);
      });

      return Object.keys(map).map((subjectName) => ({
        subjectName,
        papers: map[subjectName],
      }));
    },
  },
  watch: {
    'args.keyWord': function (val) {
      if (val && val.trim()) {
        // 有關鍵字：自動展開所有符合的群組
        this.collapsedGroups = this.groupedPapers.map(() => false);
      } else {
        // 清除關鍵字：恢復全部收合
        this.collapsedGroups = this.groupedPapers.map(() => true);
      }
    },
  },
  async activated() {
    if (!this.$route.meta.isBack) {
      this.resetData();
    }

    this.courseId = this.$route.params.courseId;
    this.courseName = this.$route.params.courseName || '';

    if (this.courseId) {
      await this.getList();
    } else {
      this.$router.push({ name: 'essayPapers' });
    }
  },
  methods: {
    // 重置資料
    resetData() {
      this.args = { keyWord: '' };
      this.papersList = [];
      this.collapsedGroups = [];
    },

    // 搜尋（前端過濾，不重新呼叫 API）
    search(isClear = 0) {
      if (isClear === 1) {
        this.args = { keyWord: '' };
      }
    },

    // 取得試卷列表
    getList() {
      if (!this.courseId) return;

      this.isLoading = true;

      const data = {
        pageIndex: 1,
        pageSize: 9999,
        data: {
          courseId: this.courseId,
        },
      };

      GetStudentEssayPapersList(data)
        .then((response) => {
          if (response.data.statusCode === 0) {
            this.papersList = response.data.data || [];

            // 初始化收合狀態（預設全部收合）
            this.collapsedGroups = this.groupedPapers.map(() => true);

            // 設定麵包屑
            this.$emit('update:currentPage', [
              {
                name: 'essayPapersList',
                title: this.courseName || response.data.data?.[0]?.courseName || '試卷列表',
                path: '',
              },
            ]);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    // 切換群組收合
    toggleGroup(index) {
      this.$set(this.collapsedGroups, index, !this.collapsedGroups[index]);
    },

    durationSecond(second) {
      return second ? Math.floor(second / 60) : '--';
    },

    // 判斷試卷是否已截止
    isExpired(paper) {
      if (!paper.endDate) return false;
      const endDateTaipei = this.$options.filters.toTaipei(paper.endDate);
      return this.$moment().isAfter(endDateTaipei);
    },

    // 取得按鈕文字
    getButtonText(paper) {
      if (this.isExpired(paper)) return '查看';
      if (paper.status === 0) return '作答去';
      if (paper.status === 1) return '繼續作答';
      return '查看';
    },

    // 點擊試卷
    handlePaperClick(paper) {
      // 已批改完成：進入成績檢視頁面
      if (this.isExpired(paper) || paper.status === 2 || paper.gradingStatus === 3) {
        this.$router.push({
          name: 'essayReview',
          params: {
            courseId: this.courseId,
            paperId: paper.essayPapersId,
          },
        });
      } else {
        // 未開始、進行中、待批改：進入作答頁面
        this.$router.push({
          name: 'essayExam',
          params: {
            courseId: this.courseId,
            paperId: paper.essayPapersId,
          },
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// 科目群組樣式
.subject-group {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background-color: #f0f4f8;
    border-left: 4px solid #4a90d9;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    margin-bottom: 12px;

    &:hover {
      background-color: #e2eaf3;
    }
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
  }

  &__count {
    font-size: 13px;
    font-weight: 400;
    color: #6c757d;
  }
}

// 試卷列表樣式
.paper-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.paper-item {
  width: 100%;
  padding: 20px;
  margin-bottom: 20px;

  border-radius: 5px;

  box-shadow: 0 5px 20px -3px rgba(#000, .2);

  &__header {
    margin-bottom: 8px;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: #212529;
    margin: 0;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__info {
    display: flex;
    gap: 24px;
    flex: 1;
  }

  &__actions {
    flex-shrink: 0;
    margin-left: 20px;
  }
}

// 手機版響應式樣式
@media (max-width: 768px) {
  .paper-item {
    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    &__content {
      flex-direction: column;
      align-items: flex-start;
    }

    &__info {
      flex-direction: column;
      gap: 8px;
      margin-bottom: 16px;
    }

    &__actions {
      margin-left: 0;
      width: 100%;

      .btn {
        width: 100%;
      }
    }
  }
}
</style>
